import { useState } from 'react';

import { useMcpConnector, useRotateCredentials } from '../hooks/use-mcp-connectors';

export interface McpConnectorEditCredentialDrawerProps {
  connectorId: string;
  onClose: () => void;
}

/**
 * Sprint A4 S1 PR 3 — Task 12. Drawer lateral para rotação de credencial.
 *
 * Consome `useMcpConnector(id)` (detail com version) + `useRotateCredentials`.
 * Envia POST /rotate-credentials com If-Match header (optimistic concurrency).
 * Aviso explícito: chave antiga preservada por 30d no Vault (auditoria) +
 * registro audit `mcp.credentials-rotated` severity 'high'.
 */
export function McpConnectorEditCredentialDrawer({
  connectorId,
  onClose,
}: McpConnectorEditCredentialDrawerProps): JSX.Element | null {
  const { data: connector } = useMcpConnector(connectorId);
  const rotateMutation = useRotateCredentials();
  const [newApiKey, setNewApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (connector === undefined) return null;

  const canSubmit = newApiKey.length >= 8 && !rotateMutation.isPending;

  function handleSubmit(): void {
    if (connector === undefined) return;
    setSubmitError(null);
    rotateMutation.mutate(
      { id: connectorId, input: { newApiKey }, version: connector.version },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (err: Error) => {
          setSubmitError(err.message);
        },
      },
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50">
      <div className="h-full w-full max-w-md overflow-y-auto bg-surface p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">
            Trocar credencial · {connector.displayName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={rotateMutation.isPending}
            aria-label="Fechar"
            className="text-text-secondary hover:text-text-primary disabled:opacity-50"
          >
            ✕
          </button>
        </div>

        <div className="mt-4 rounded bg-amber-50 p-3 text-xs text-amber-900">
          A credencial atual será substituída. A antiga fica preservada no cofre por 30 dias
          (auditoria). Esta ação registra um audit event de severidade <strong>alta</strong>.
        </div>

        <div className="mt-4">
          <label htmlFor="rotate-newApiKey" className="block text-sm font-medium text-text-primary">
            Nova chave de API *
          </label>
          <div className="mt-1 flex gap-2">
            <input
              id="rotate-newApiKey"
              type={showKey ? 'text' : 'password'}
              value={newApiKey}
              onChange={(e) => setNewApiKey(e.target.value)}
              className="flex-1 rounded border border-border-subtle px-3 py-2 font-mono text-sm"
            />
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="rounded border border-border-subtle px-3 py-2 text-xs hover:bg-bg-muted"
            >
              {showKey ? '🙈' : '👁'}
            </button>
          </div>
          <p className="mt-1 text-xs text-text-secondary">Mínimo 8 caracteres.</p>
        </div>

        {submitError !== null && (
          <div className="mt-4 rounded bg-red-50 p-3 text-xs text-red-900">
            <strong>Erro:</strong> {submitError}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={rotateMutation.isPending}
            className="rounded border border-border-subtle px-4 py-2 text-sm hover:bg-bg-muted disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded bg-accent px-4 py-2 text-sm text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {rotateMutation.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
